import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Facebook } from '@freelancer/facebook';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { catchError, map, take } from 'rxjs/operators';
import { transformAuthResponse, transformResponseData, } from './login-signup.transformers';
import * as i0 from "@angular/core";
import * as i1 from "../auth/auth.service";
import * as i2 from "@angular/common/http";
import * as i3 from "../freelancer-http/freelancer-http.service";
import * as i4 from "@angular/router";
import * as i5 from "../facebook/facebook.service";
import * as i6 from "../location/location.service";
/**
 * Service which provides an interface to backend endpoints for the login/signup funnel
 */
var LoginSignupService = /** @class */ (function () {
    function LoginSignupService(auth, rawHttp, freelancerHttp, router, facebook, location) {
        this.auth = auth;
        this.rawHttp = rawHttp;
        this.freelancerHttp = freelancerHttp;
        this.router = router;
        this.facebook = facebook;
        this.location = location;
    }
    LoginSignupService.prototype.login = function (user, password) {
        var _this = this;
        return this.auth.deviceToken$
            .pipe(take(1))
            .toPromise()
            .then(function (deviceToken) {
            // return a custom error because passing undefined
            // to the backend will result in `InvalidInput`
            if (!deviceToken) {
                return {
                    status: 'error',
                    errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
                };
            }
            return _this.freelancerHttp
                .post('auth/login.php', {
                user: user,
                password: password,
                device_token: deviceToken,
            }, {
                isGaf: true,
                serializeBody: true,
            })
                .pipe(take(1), map(function (responseData) {
                return transformResponseData(responseData, transformAuthResponse);
            }))
                .toPromise();
        });
    };
    LoginSignupService.prototype.verify = function (preLoginToken, password) {
        var _this = this;
        return this.auth.deviceToken$
            .pipe(take(1))
            .toPromise()
            .then(function (deviceToken) {
            if (!deviceToken) {
                return {
                    status: 'error',
                    errorCode: ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID,
                };
            }
            return _this.freelancerHttp
                .post('auth/verify2FALogin.php', {
                totp: password,
                device_token: deviceToken,
                pre_login_token: preLoginToken,
            }, {
                isGaf: true,
                serializeBody: true,
            })
                .pipe(take(1))
                .toPromise();
        });
    };
    LoginSignupService.prototype.facebookLogin = function () {
        var _this = this;
        return this.facebook.login().then(function (response) {
            if (response.status !== 'success') {
                return {
                    status: 'error',
                    errorCode: ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED,
                };
            }
            return _this.freelancerHttp
                .post('auth/facebookLogin.php', {
                app_id: _this.facebook.appId,
                credentials: response.result.signedRequest,
                access_token: response.result.accessToken,
            }, {
                isGaf: true,
                serializeBody: true,
            })
                .pipe(take(1))
                .toPromise();
        });
    };
    LoginSignupService.prototype.checkUserDetails = function (user, password) {
        return this.freelancerHttp
            .post('users/0.1/users/check', {
            user: user,
            password: password,
        })
            .pipe(take(1))
            .toPromise();
    };
    /**
     * Handle successful authentication, be it on login or signup
     */
    LoginSignupService.prototype.handleSuccess = function (action, userId, authToken, rememberLogin, redirectParams) {
        var _this = this;
        // set auth session (automatically sets cookies)
        this.auth.setSession(userId.toString(), authToken);
        var crossDomainSSOPromise = this.setCrossDomainSSO(userId, authToken, rememberLogin);
        var redirectPromise = action === 'login'
            ? this.getLoginRedirectUrl(redirectParams)
            : // TODO: get signup URL
                Promise.resolve('/new-freelancer');
        // wait for both reqest chains to finish, then redirect
        Promise.all([redirectPromise, crossDomainSSOPromise]).then(function (_a) {
            var redirectUrl = _a[0];
            if (redirectUrl === 'reload') {
                // TODO: reload?
            }
            if (redirectUrl.startsWith('http')) {
                // absolute URL: do a hard navigate
                return _this.location.redirect(redirectUrl);
            }
            _this.router.navigate([redirectUrl]);
        });
    };
    /**
     * Logs a user in on other domains that they should be logged in on.
     * Fetches domains from `getCrossDomainSSODomains.php`, then sends requests
     * to those domains to set cookies on them.
     */
    LoginSignupService.prototype.setCrossDomainSSO = function (userId, authToken, rememberLogin) {
        var _this = this;
        // this is equivalent to the `serializeBody` option for freelancerHttp
        // needs to be manually done since we're using raw httpClient for the other domains
        var formBody = this.freelancerHttp._serialize({
            userId: userId,
            authToken: authToken,
            rememberLogin: rememberLogin,
        });
        var headers = new HttpHeaders().append('Content-Type', 'application/x-www-form-urlencoded');
        return this.freelancerHttp
            .get('auth/getCrossDomainSSODomains.php', { isGaf: true })
            .pipe(take(1))
            .toPromise()
            .then(function (response) {
            if (response.status === 'error') {
                return;
            }
            return Promise.all(response.result.domains.map(function (domain) {
                return _this.rawHttp
                    .post(domain + "/ajax-api/auth/crossDomainSSO.php", formBody, {
                    headers: headers,
                    withCredentials: true,
                })
                    .pipe(
                // silence any errors
                catchError(function (_) { return Promise.resolve(); }), take(1))
                    .toPromise();
            })).then();
        });
    };
    /**
     * Returns the next route to load after logging in
     */
    LoginSignupService.prototype.getLoginRedirectUrl = function (_a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.isModal, isModal = _c === void 0 ? false : _c, _d = _b.redirectDisabled, redirectDisabled = _d === void 0 ? false : _d, next = _b.next, goto = _b.goto, projectCollaborationInvitation = _b.projectCollaborationInvitation, contestCollaborationInvitation = _b.contestCollaborationInvitation;
        return this.freelancerHttp
            .post('auth/getLoginRedirectUrl.php', {
            is_modal: isModal.toString(),
            redirect_disabled: redirectDisabled.toString(),
            next: next,
            goto: goto,
            project_collaboration_invitation: projectCollaborationInvitation,
            contest_collaboration_invitation: contestCollaborationInvitation,
        }, { isGaf: true, serializeBody: true })
            .pipe(take(1))
            .toPromise()
            .then(function (response) {
            return response.status === 'success'
                ? response.result.redirect_url
                : // default to dashboard for login
                    'dashboard';
        });
    };
    LoginSignupService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function LoginSignupService_Factory() { return new LoginSignupService(i0.ɵɵinject(i1.Auth), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.FreelancerHttp), i0.ɵɵinject(i4.Router), i0.ɵɵinject(i5.Facebook), i0.ɵɵinject(i6.Location)); }, token: LoginSignupService, providedIn: "root" });
    return LoginSignupService;
}());
export { LoginSignupService };
